<template>
  <div :class="$attrs.class">
    <label class="block text-sm font-medium text-gray-600 mb-1.5">{{ label }}</label>

    <!-- Telefonnummer field with country code selector -->
    <div v-if="fieldKey === 'telefonnummer'" class="flex gap-2">
      <select
        :value="countryCode"
        @change="onCountryChange"
        @blur="onBlur"
        class="py-2.5 px-3 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary rounded-xl bg-white transition-colors text-gray-900 text-sm"
      >
        <option v-for="c in COUNTRY_CODES" :key="c.code" :value="c.code">{{ c.label }}</option>
      </select>
      <input
        :value="localPhone"
        @input="onPhoneInput"
        type="tel"
        :autocomplete="autocomplete"
        @blur="onBlur"
        class="flex-1 py-2.5 px-4 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary rounded-xl bg-white transition-colors text-gray-900 text-sm"
      />
    </div>

    <!-- Default field -->
    <component
      v-else
      :is="type === 'textarea' ? 'textarea' : 'input'"
      :value="modelValue"
      @input="updateValue($event.target.value)"
      :type="type"
      :autocomplete="autocomplete"
      :placeholder="placeholder"
      @blur="onBlur"
      class="w-full py-2.5 px-4 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary rounded-xl bg-white transition-colors text-gray-900 text-sm placeholder:text-gray-400"
      :rows="type === 'textarea' ? 3 : undefined"
    />

    <p v-if="hint" class="text-gray-500 text-xs mt-1.5">{{ hint }}</p>
    <p v-if="touched && error" class="text-red-500 text-xs mt-1.5">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'

const COUNTRY_CODES = [
  { code: '+43', label: '🇦🇹 +43' },
  { code: '+49', label: '🇩🇪 +49' },
  { code: '+41', label: '🇨🇭 +41' },
  { code: '+33', label: '🇫🇷 +33' },
  { code: '+39', label: '🇮🇹 +39' },
  { code: '+31', label: '🇳🇱 +31' },
  { code: '+32', label: '🇧🇪 +32' },
  { code: '+34', label: '🇪🇸 +34' },
  { code: '+45', label: '🇩🇰 +45' },
  { code: '+46', label: '🇸🇪 +46' },
  { code: '+44', label: '🇬🇧 +44' },
]

const props = defineProps({
  modelValue: String,
  label: String,
  type: { type: String, default: 'text' },
  placeholder: String,
  hint: String,
  error: String,
  touched: Boolean,
  autocomplete: { type: String, default: '' },
  fieldKey: { type: String, default: '' },
  defaultCountryCode: { type: String, default: null }
})

const COUNTRY_MAP = { de: '+49', at: '+43' }

function resolveDefaultCode() {
  if (props.defaultCountryCode) {
    return COUNTRY_MAP[props.defaultCountryCode.toLowerCase()] || '+43'
  }
  return '+43'
}

const emit = defineEmits(['update:modelValue', 'blur'])

const countryCode = ref(resolveDefaultCode())
const localPhone = ref('')

watch(() => props.modelValue, (val) => {
  if (props.fieldKey !== 'telefonnummer') return
  for (const { code } of COUNTRY_CODES) {
    if (val?.startsWith(code)) {
      countryCode.value = code
      localPhone.value = val.slice(code.length)
      return
    }
  }
  localPhone.value = val || ''
}, { immediate: true })

function onCountryChange(e) {
  countryCode.value = e.target.value
  emit('update:modelValue', localPhone.value ? countryCode.value + localPhone.value : '')
}

function onPhoneInput(e) {
  localPhone.value = e.target.value
  emit('update:modelValue', localPhone.value ? countryCode.value + localPhone.value : '')
}

function updateValue(val) {
  emit('update:modelValue', val)
}

function onBlur() {
  emit('blur')
}
</script>